import React, { useState, useEffect, useRef } from 'react'

export default function Topbar({ title = 'Admin', right, onMenu, onLogout, userEmail, userRole, onSettingsClick, logoUrl }) {
    const [open, setOpen] = useState(false)
    const popupRef = useRef(null)
    const initials = (userEmail || 'U').slice(0, 1).toUpperCase()

    // Map database role values to display values
    const getDisplayRole = (role) => {
        switch (role) {
            case 'SUPER_ADMIN':
                return 'Super Admin'
            case 'ADMIN':
                return 'Admin'
            case 'VIEWER':
                return 'Viewer'
            default:
                return 'Admin'
        }
    }
    
    const displayRole = getDisplayRole(userRole)

    // Close popup when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setOpen(false)
            }
        }

        if (open) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [open])

    return (
        <div className="row-between" style={{ height: 56, padding: '0 16px', background: 'transparent', boxShadow: 'none', borderRadius: 0, position: 'relative', borderBottom: '1px solid #e5e7eb' }}>
            <div className="row gap-12" style={{ alignItems: 'center' }}>
                <button className="btn show-sm" onClick={onMenu} aria-label="Open menu" style={{ background: '#e5e7eb' }}>☰</button>
                {logoUrl ? (
                    <img 
                        src={logoUrl} 
                        alt="Logo" 
                        style={{ 
                            height: 60, 
                            maxWidth: 350, 
                            width: 'auto', 
                            objectFit: 'contain'
                        }} 
                        onError={(e) => { e.target.style.display = 'none' }} 
                    />
                ) : (
                    <h3 className="title" style={{ fontSize: 20 }}>{title}</h3>
                )}
            </div>
            <div className="row gap-8" style={{ position: 'relative' }}>
                {right}
                <button
                    className="btn btn-ghost"
                    aria-label="Profile"
                    onClick={() => setOpen((v) => !v)}
                    style={{ width: 36, height: 36, borderRadius: '50%', padding: 0 }}
                >
                    {initials}
                </button>
                {open && (
                    <div ref={popupRef} style={{ 
                        position: 'absolute', 
                        right: 0, 
                        top: 52, 
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
                        border: '1px solid rgba(226, 232, 240, 0.8)', 
                        borderRadius: 16, 
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.05)', 
                        padding: '12px 0', 
                        minWidth: 200, 
                        zIndex: 1000,
                        transform: 'translateY(8px)',
                        animation: 'fadeInUp 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                    }}>
                        {/* User Info Header */}
                        <div style={{ 
                            padding: '12px 16px 8px 16px', 
                            borderBottom: '1px solid rgba(226, 232, 240, 0.6)',
                            marginBottom: 8
                        }}>
                            <div style={{ 
                                fontSize: 13, 
                                fontWeight: 600, 
                                color: '#64748b', 
                                textTransform: 'uppercase', 
                                letterSpacing: '0.5px',
                                marginBottom: 4
                            }}>
                                Account
                            </div>
                            <div style={{ 
                                fontSize: 14, 
                                fontWeight: 500, 
                                color: '#1e293b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                            }}>
                                <div style={{ 
                                    width: 24, 
                                    height: 24, 
                                    borderRadius: '50%', 
                                    background: displayRole === 'Super Admin' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: 12,
                                    fontWeight: 600
                                }}>
                                    {initials}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <span>{userEmail || 'User Account'}</span>
                                    <span style={{ 
                                        fontSize: 12, 
                                        fontWeight: 600, 
                                        color: displayRole === 'Super Admin' ? '#f59e0b' : '#3b82f6',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        {displayRole}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Menu Items */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <button 
                                style={{ 
                                    width: '100%', 
                                    height: 44, 
                                    padding: '0 16px', 
                                    fontSize: 14, 
                                    fontWeight: 500,
                                    justifyContent: 'flex-start',
                                    borderRadius: 0,
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#475569',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                    const isSuperAdmin = displayRole === 'Super Admin';
                                    const bgColor = isSuperAdmin ? 'rgba(245, 158, 11, 0.08)' : 'rgba(59, 130, 246, 0.08)';
                                    const borderColor = isSuperAdmin ? '#f59e0b' : '#3b82f6';
                                    const textColor = isSuperAdmin ? '#d97706' : '#1e40af';
                                    
                                    e.target.style.background = `linear-gradient(90deg, ${bgColor} 0%, ${bgColor.replace('0.08', '0.04')} 100%)`;
                                    e.target.style.color = textColor;
                                    e.target.style.borderLeft = `3px solid ${borderColor}`;
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'transparent';
                                    e.target.style.color = '#475569';
                                    e.target.style.borderLeft = '3px solid transparent';
                                }}
                                onClick={() => { setOpen(false); onSettingsClick && onSettingsClick() }}
                            >
                                <div style={{ 
                                    width: 20, 
                                    height: 20, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    background: displayRole === 'Super Admin' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                                    borderRadius: 6
                                }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="12" cy="7" r="4"></circle>
                                    </svg>
                                </div>
                                <span>{displayRole}</span>
                            </button>
                            
                            <div style={{ 
                                height: 1, 
                                background: 'linear-gradient(90deg, transparent 0%, rgba(226, 232, 240, 0.6) 50%, transparent 100%)',
                                margin: '4px 16px'
                            }}></div>
                            
                            <button 
                                style={{ 
                                    width: '100%', 
                                    height: 44, 
                                    padding: '0 16px', 
                                    fontSize: 14, 
                                    fontWeight: 500,
                                    justifyContent: 'flex-start',
                                    borderRadius: 0,
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#475569',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = 'linear-gradient(90deg, rgba(239, 68, 68, 0.08) 0%, rgba(239, 68, 68, 0.04) 100%)';
                                    e.target.style.color = '#dc2626';
                                    e.target.style.borderLeft = '3px solid #ef4444';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'transparent';
                                    e.target.style.color = '#475569';
                                    e.target.style.borderLeft = '3px solid transparent';
                                }}
                                onClick={() => { setOpen(false); onLogout && onLogout() }}
                            >
                                <div style={{ 
                                    width: 20, 
                                    height: 20, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    borderRadius: 6
                                }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                        <polyline points="16,17 21,12 16,7"></polyline>
                                        <line x1="21" y1="12" x2="9" y2="12"></line>
                                    </svg>
                                </div>
                                <span>Logout</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
